import React, { useState, useContext, useEffect, useCallback, useMemo } from 'react';
import { UserContext } from '../context/UserContext';
import { OnboardingContext } from '../context/OnboardingContext';
import OnboardingWizard from './OnboardingWizard';
import GoalSelector from './GoalSelector';
import HotspotTour from './HotspotTour';
import AchievementNotification from './AchievementNotification';
import ConfettiCelebration from './ConfettiCelebration';
import { useOnboardingAnalytics } from '../hooks/useOnboardingAnalytics';
import { useAchievements } from '../hooks/useAchievements';
import { useOnboardingEmails, EMAIL_EVENTS } from '../hooks/useOnboardingEmails';
import { usePersonalizedRecommendations } from '../hooks/usePersonalizedRecommendations';
import { USER_GOALS } from '../config/onboardingMedia';
import api from '../utils/api';

// Storage keys
const STORAGE_KEYS = {
  PROGRESS: 'onboarding_progress',
  PREFERENCES: 'onboarding_preferences',
  DISMISSED_TIPS: 'onboarding_dismissed_tips',
  CHECKLIST: 'onboarding_checklist',
  SELECTED_GOAL: 'onboarding_selected_goal',
  TOUR_MODE: 'onboarding_tour_mode',
  TOUR_DEPTH: 'onboarding_tour_depth',
};

// Default checklist items
const DEFAULT_CHECKLIST = [
  { id: 'completeProfile', completed: false },
  { id: 'exploreDashboard', completed: false },
  { id: 'trySearch', completed: false },
  { id: 'viewBudget', completed: false },
  { id: 'setNotifications', completed: false },
];

// Default preferences
const DEFAULT_PREFERENCES = {
  showChecklist: true,
  showTips: true,
  locale: 'en',
  tourMode: 'wizard', // 'wizard' or 'hotspot'
  audioEnabled: true,
};

/**
 * OnboardingProvider - Manages the complete onboarding experience
 *
 * Features:
 * - Wizard display for new users
 * - Progress persistence (localStorage + backend)
 * - Checklist state management
 * - Contextual tips
 * - User preferences
 * - Analytics integration
 * - Gamification/Achievements
 * - Branching paths with goals
 * - Hotspot tour alternative
 * - Email drip campaign integration
 * - Personalized recommendations
 */
const OnboardingProvider = ({ children }) => {
  const { user } = useContext(UserContext);

  // Wizard state
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);

  // Goal selection state (branching paths)
  const [showGoalSelector, setShowGoalSelector] = useState(false);
  const [selectedGoal, setSelectedGoal] = useState(null);

  // Tour mode state
  const [tourMode, setTourMode] = useState('wizard'); // 'wizard' or 'hotspot'
  const [showHotspotTour, setShowHotspotTour] = useState(false);

  // Tour depth state - 'lite' for quick overview, 'detailed' for step-by-step
  const [tourDepth, setTourDepth] = useState('lite');

  // Checklist state
  const [checklistItems, setChecklistItems] = useState([]);
  const [checklistVisible, setChecklistVisible] = useState(true);

  // Contextual tips state
  const [activeTip, setActiveTip] = useState(null);
  const [dismissedTips, setDismissedTips] = useState([]);

  // Preferences
  const [preferences, setPreferences] = useState(DEFAULT_PREFERENCES);

  // Interactive highlights
  const [highlightElement, setHighlightElement] = useState(null);

  // Achievement notification state
  const [pendingAchievement, setPendingAchievement] = useState(null);

  // Confetti celebration state
  const [showConfetti, setShowConfetti] = useState(false);

  // Analytics - destructure for stable references
  const analyticsHook = useOnboardingAnalytics(user?.id);
  const {
    trackStepView,
    trackStepAction,
    trackComplete,
    trackSkip,
    trackTipInteraction,
    trackChecklistItem,
    trackInteractiveFeature,
  } = analyticsHook;

  // Achievements/Gamification - destructure for stable references
  const achievementsHook = useAchievements(user?.id);
  const {
    unlockedAchievements,
    totalPoints: achievementTotalPoints,
    unlock: unlockAchievement,
    trackAction: achievementTrackAction,
    getAchievement,
  } = achievementsHook;

  // Email drip campaign - destructure for stable references
  const emailTrackingHook = useOnboardingEmails(user?.id);
  const { trackEvent: trackEmailEvent } = emailTrackingHook;

  // Personalized recommendations - destructure for stable references
  // Note: The hook internally memoizes userRoles to prevent infinite loops
  const recommendationsHook = usePersonalizedRecommendations(user?.id, user?.role_slugs);
  const {
    recommendations: recommendationsList,
    engagementScore: recommendationEngagementScore,
  } = recommendationsHook;

  // Load preferences from localStorage
  const loadPreferences = useCallback(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEYS.PREFERENCES);
      if (saved) {
        const parsed = JSON.parse(saved);
        setPreferences({ ...DEFAULT_PREFERENCES, ...parsed });
      }
    } catch (e) {
      console.error('Failed to load onboarding preferences:', e);
    }
  }, []);

  // Save preferences to localStorage
  const savePreferences = useCallback((prefs) => {
    try {
      localStorage.setItem(STORAGE_KEYS.PREFERENCES, JSON.stringify(prefs));
    } catch (e) {
      console.error('Failed to save onboarding preferences:', e);
    }
  }, []);

  // Load dismissed tips
  const loadDismissedTips = useCallback(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEYS.DISMISSED_TIPS);
      if (saved) {
        setDismissedTips(JSON.parse(saved));
      }
    } catch (e) {
      console.error('Failed to load dismissed tips:', e);
    }
  }, []);

  // Save dismissed tips
  const saveDismissedTips = useCallback((tips) => {
    try {
      localStorage.setItem(STORAGE_KEYS.DISMISSED_TIPS, JSON.stringify(tips));
    } catch (e) {
      console.error('Failed to save dismissed tips:', e);
    }
  }, []);

  // Load checklist state
  const loadChecklist = useCallback(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEYS.CHECKLIST);
      if (saved) {
        const parsed = JSON.parse(saved);
        // Merge with default to handle new items
        const merged = DEFAULT_CHECKLIST.map(item => ({
          ...item,
          completed: parsed.find(p => p.id === item.id)?.completed || false,
        }));
        setChecklistItems(merged);
      } else {
        setChecklistItems(DEFAULT_CHECKLIST);
      }
    } catch (e) {
      console.error('Failed to load checklist:', e);
      setChecklistItems(DEFAULT_CHECKLIST);
    }
  }, []);

  // Save checklist state
  const saveChecklist = useCallback((items) => {
    try {
      localStorage.setItem(STORAGE_KEYS.CHECKLIST, JSON.stringify(items));
    } catch (e) {
      console.error('Failed to save checklist:', e);
    }
  }, []);

  // Load saved progress
  const loadProgress = useCallback(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEYS.PROGRESS);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (parsed.userId === user?.id && Date.now() - parsed.timestamp < 24 * 60 * 60 * 1000) {
          return parsed;
        }
      }
    } catch (e) {
      console.error('Failed to load onboarding progress:', e);
    }
    return null;
  }, [user?.id]);

  // Save progress
  const saveProgress = useCallback((step) => {
    try {
      localStorage.setItem(STORAGE_KEYS.PROGRESS, JSON.stringify({
        step,
        timestamp: Date.now(),
        userId: user?.id,
      }));
    } catch (e) {
      console.error('Failed to save onboarding progress:', e);
    }
  }, [user?.id]);

  // Clear progress
  const clearProgress = useCallback(() => {
    try {
      localStorage.removeItem(STORAGE_KEYS.PROGRESS);
    } catch (e) {
      // Ignore
    }
  }, []);

  // Initialize on mount
  useEffect(() => {
    loadPreferences();
    loadDismissedTips();
    loadChecklist();
  }, [loadPreferences, loadDismissedTips, loadChecklist]);

  // Load selected goal from localStorage
  const loadSelectedGoal = useCallback(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEYS.SELECTED_GOAL);
      if (saved) {
        setSelectedGoal(saved);
      }
      // Also load tour depth
      const savedDepth = localStorage.getItem(STORAGE_KEYS.TOUR_DEPTH);
      if (savedDepth) {
        setTourDepth(savedDepth);
      }
    } catch (e) {
      console.error('Failed to load selected goal:', e);
    }
  }, []);

  // Save selected goal and tour depth
  const saveSelectedGoal = useCallback((goalId, depth = 'lite') => {
    try {
      localStorage.setItem(STORAGE_KEYS.SELECTED_GOAL, goalId);
      localStorage.setItem(STORAGE_KEYS.TOUR_DEPTH, depth);
      setSelectedGoal(goalId);
      setTourDepth(depth);
    } catch (e) {
      console.error('Failed to save selected goal:', e);
    }
  }, []);

  // Check if user needs onboarding
  useEffect(() => {
    if (!user) {
      setShowOnboarding(false);
      setShowGoalSelector(false);
      return;
    }

    const settings = user.settings || {};
    const hasCompletedOnboarding = settings.onboarding_completed === true;
    const hasSkippedOnboarding = settings.onboarding_skipped === true;

    // Load any previously selected goal
    loadSelectedGoal();

    // Show onboarding for new users who haven't completed or skipped it
    if (!hasCompletedOnboarding && !hasSkippedOnboarding) {
      // Check for saved progress
      const savedProgress = loadProgress();
      if (savedProgress?.step > 0) {
        setCurrentStep(savedProgress.step);
        // If they have progress, skip goal selector
        const timer = setTimeout(() => setShowOnboarding(true), 500);
        return () => clearTimeout(timer);
      }

      // Show goal selector first for new users
      const timer = setTimeout(() => setShowGoalSelector(true), 500);
      return () => clearTimeout(timer);
    }

    // Show checklist if onboarding is complete but checklist is not
    if (hasCompletedOnboarding && preferences.showChecklist) {
      setChecklistVisible(true);
    }
  }, [user, loadProgress, loadSelectedGoal, preferences.showChecklist]);

  // Goal selection handler
  const handleGoalSelect = useCallback((goal, depth = 'lite') => {
    const goalId = typeof goal === 'string' ? goal : goal?.id;
    if (!goalId) return;

    saveSelectedGoal(goalId, depth);
    setShowGoalSelector(false);

    // Track the goal selection
    trackStepAction('goal_selection', 'selected', { goalId, tourDepth: depth });

    // Determine tour mode based on preference
    if (preferences.tourMode === 'hotspot') {
      setShowHotspotTour(true);
    } else {
      setShowOnboarding(true);
    }

    // Track wizard started for email drip
    trackEmailEvent(EMAIL_EVENTS.WIZARD_STARTED, { goalId, tourDepth: depth });

    // Unlock first steps achievement
    unlockAchievement('first_steps');
  }, [saveSelectedGoal, preferences.tourMode, trackStepAction, trackEmailEvent, unlockAchievement]);

  // Wizard handlers
  const handleComplete = useCallback(() => {
    setShowOnboarding(false);
    setShowHotspotTour(false);
    clearProgress();
    setChecklistVisible(true);

    // Trigger confetti celebration
    setShowConfetti(true);

    // Unlock tour complete achievement
    unlockAchievement('tour_complete');

    // Track completion for email
    trackEmailEvent(EMAIL_EVENTS.WIZARD_COMPLETED, { selectedGoal });

    // Show achievement notification (with slight delay for confetti to start)
    setTimeout(() => {
      const achievement = getAchievement('tour_complete');
      if (achievement) {
        setPendingAchievement(achievement);
      }
    }, 500);
  }, [clearProgress, unlockAchievement, getAchievement, trackEmailEvent, selectedGoal]);

  const handleSkip = useCallback(() => {
    setShowOnboarding(false);
    setShowHotspotTour(false);
    clearProgress();

    // Track skip for email drip
    trackEmailEvent(EMAIL_EVENTS.WIZARD_SKIPPED, { skippedAtStep: currentStep });
  }, [clearProgress, trackEmailEvent, currentStep]);

  // Trigger onboarding manually
  const triggerOnboarding = useCallback((mode = 'wizard') => {
    setCurrentStep(0);
    if (mode === 'hotspot') {
      setShowHotspotTour(true);
    } else {
      setShowOnboarding(true);
    }
  }, []);

  // Restart the tour from the beginning (resets completion status)
  const restartTour = useCallback(async (options = {}) => {
    const { mode = 'wizard', showGoalSelector: showGoals = true } =
      typeof options === 'string' ? { mode: options } : options;

    try {
      // Reset the backend onboarding status
      await api.patch('/users/me/settings', {
        onboarding_completed: false,
        onboarding_skipped: false,
        onboarding_completed_at: null,
      });

      // Clear local progress
      clearProgress();

      // Reset to step 0
      setCurrentStep(0);

      // Clear selected goal and tour depth to show goal selector again
      if (showGoals) {
        setSelectedGoal(null);
        setTourDepth('lite');
        localStorage.removeItem(STORAGE_KEYS.SELECTED_GOAL);
        localStorage.removeItem(STORAGE_KEYS.TOUR_DEPTH);
        setShowGoalSelector(true);
      } else {
        // Start the tour directly without goal selector
        if (mode === 'hotspot') {
          setShowHotspotTour(true);
        } else {
          setShowOnboarding(true);
        }
      }

      return true;
    } catch (err) {
      console.error('Failed to restart tour:', err);
      return false;
    }
  }, [clearProgress]);

  // Checklist handlers
  const toggleChecklist = useCallback(() => {
    setChecklistVisible(prev => !prev);
  }, []);

  const completeChecklistItem = useCallback((itemId) => {
    setChecklistItems(prev => {
      const updated = prev.map(item =>
        item.id === itemId ? { ...item, completed: true } : item
      );
      saveChecklist(updated);
      trackChecklistItem(itemId, true);

      // Check for checklist achievements
      const completedCount = updated.filter(i => i.completed).length;
      const totalCount = updated.length;

      if (completedCount === 1) {
        unlockAchievement('checklist_starter');
      }
      if (completedCount >= Math.ceil(totalCount / 2)) {
        unlockAchievement('checklist_half');
      }
      if (completedCount === totalCount) {
        unlockAchievement('checklist_complete');
        trackEmailEvent(EMAIL_EVENTS.CHECKLIST_COMPLETED);
      }

      return updated;
    });
  }, [saveChecklist, trackChecklistItem, unlockAchievement, trackEmailEvent]);

  const isChecklistComplete = useMemo(() => {
    return checklistItems.length > 0 && checklistItems.every(item => item.completed);
  }, [checklistItems]);

  // Tip handlers
  const showTip = useCallback((tipId) => {
    if (!dismissedTips.includes(tipId)) {
      setActiveTip(tipId);
    }
  }, [dismissedTips]);

  const dismissTip = useCallback((tipId, permanent = false) => {
    setActiveTip(null);
    if (permanent) {
      const updated = [...dismissedTips, tipId];
      setDismissedTips(updated);
      saveDismissedTips(updated);
      trackTipInteraction(tipId, 'dismissed_permanently');
    } else {
      trackTipInteraction(tipId, 'dismissed');
    }
  }, [dismissedTips, saveDismissedTips, trackTipInteraction]);

  // Preference handlers
  const updatePreferences = useCallback((updates) => {
    setPreferences(prev => {
      const updated = { ...prev, ...updates };
      savePreferences(updated);

      // Sync to backend if user is logged in
      if (user?.id) {
        api.patch('/users/me/settings', {
          onboarding_preferences: updated,
        }).catch(err => {
          console.error('Failed to sync preferences to backend:', err);
        });
      }

      return updated;
    });
  }, [savePreferences, user?.id]);

  // Switch tour mode (must be after updatePreferences)
  const switchTourMode = useCallback((mode) => {
    setTourMode(mode);
    updatePreferences({ tourMode: mode });
    if (showOnboarding || showHotspotTour) {
      if (mode === 'hotspot') {
        setShowOnboarding(false);
        setShowHotspotTour(true);
      } else {
        setShowHotspotTour(false);
        setShowOnboarding(true);
      }
    }
  }, [showOnboarding, showHotspotTour, updatePreferences]);

  // Highlight handlers
  const clearHighlight = useCallback(() => {
    setHighlightElement(null);
  }, []);

  // Memoize saved progress to avoid calling loadProgress() on every render
  const savedProgress = useMemo(() => loadProgress(), [loadProgress]);

  // Context value - use extracted stable references instead of hook objects
  const contextValue = useMemo(() => ({
    // Wizard controls
    triggerOnboarding,
    restartTour,
    isOnboardingActive: showOnboarding,
    isHotspotTourActive: showHotspotTour,
    currentStep,
    setCurrentStep,
    switchTourMode,
    tourMode,

    // Goal selection
    selectedGoal,
    selectGoal: handleGoalSelect,
    availableGoals: USER_GOALS,

    // Tour depth - 'lite' or 'detailed'
    tourDepth,

    // Progress persistence
    savedProgress,
    saveProgress,
    clearProgress,

    // Checklist
    checklistItems,
    checklistVisible,
    toggleChecklist,
    completeChecklistItem,
    isChecklistComplete,

    // Contextual tips
    activeTip,
    showTip,
    dismissTip,
    dismissedTips,

    // Preferences
    preferences,
    updatePreferences,

    // Interactive highlights
    highlightElement,
    setHighlightElement,
    clearHighlight,

    // Analytics - individual functions for stable references
    trackStepView,
    trackStepAction,
    trackComplete,
    trackSkip,
    trackTipInteraction,
    trackChecklistItem,
    trackInteractiveFeature,

    // Achievements/Gamification
    achievements: unlockedAchievements,
    totalPoints: achievementTotalPoints,
    unlockAchievement,
    trackAction: achievementTrackAction,

    // Personalized recommendations
    recommendations: recommendationsList,
    engagementScore: recommendationEngagementScore,

    // Email tracking
    trackEmailEvent,
  }), [
    triggerOnboarding,
    restartTour,
    showOnboarding,
    showHotspotTour,
    currentStep,
    switchTourMode,
    tourMode,
    selectedGoal,
    handleGoalSelect,
    tourDepth,
    savedProgress,
    saveProgress,
    clearProgress,
    checklistItems,
    checklistVisible,
    toggleChecklist,
    completeChecklistItem,
    isChecklistComplete,
    activeTip,
    showTip,
    dismissTip,
    dismissedTips,
    preferences,
    updatePreferences,
    highlightElement,
    clearHighlight,
    trackStepView,
    trackStepAction,
    trackComplete,
    trackSkip,
    trackTipInteraction,
    trackChecklistItem,
    trackInteractiveFeature,
    unlockedAchievements,
    achievementTotalPoints,
    unlockAchievement,
    achievementTrackAction,
    recommendationsList,
    recommendationEngagementScore,
    trackEmailEvent,
  ]);

  // Handle achievement notification dismiss
  const handleAchievementDismiss = useCallback(() => {
    setPendingAchievement(null);
  }, []);

  // Handle confetti completion
  const handleConfettiComplete = useCallback(() => {
    setShowConfetti(false);
  }, []);

  return (
    <OnboardingContext.Provider value={contextValue}>
      {children}

      {/* Goal Selector Modal */}
      {showGoalSelector && user && (
        <GoalSelector
          userRoles={user.role_slugs || []}
          onSelectGoal={handleGoalSelect}
          onSkip={() => {
            setShowGoalSelector(false);
            setShowOnboarding(true);
          }}
        />
      )}

      {/* Wizard Mode */}
      {showOnboarding && user && (
        <OnboardingWizard
          onComplete={handleComplete}
          onSkip={handleSkip}
          initialStep={currentStep}
          locale={preferences.locale}
          selectedGoal={selectedGoal}
          tourDepth={tourDepth}
        />
      )}

      {/* Hotspot Tour Mode */}
      {showHotspotTour && user && (
        <HotspotTour
          selectedGoal={selectedGoal}
          onComplete={handleComplete}
          onSkip={handleSkip}
          locale={preferences.locale}
        />
      )}

      {/* Achievement Notification */}
      {pendingAchievement && (
        <AchievementNotification
          achievement={pendingAchievement}
          onDismiss={handleAchievementDismiss}
        />
      )}

      {/* Confetti Celebration */}
      <ConfettiCelebration
        active={showConfetti}
        duration={4000}
        pieceCount={120}
        onComplete={handleConfettiComplete}
      />
    </OnboardingContext.Provider>
  );
};

export default OnboardingProvider;
